import { useCallback, useMemo } from "react"
import { numberFormat } from "shared/util"
import { Box, Button, Checkbox, Divider, ListItem, ListItemButton, ListItemText, Slide, Stack, Typography } from "@mui/material"
import { Replay as ReplayIcon } from '@mui/icons-material'

import { GridColumnDef, GridColumnDefMap, GridFilter, GridWeekDays } from "../../types"
import { isFilterd } from "../../util"

const DayOfWeeks: GridWeekDays[] = [
	'月', '火', '水', '木', '金', '土', '日'
]
const HourOfDays = [
	0, 3, 6, 9 , 12, 15, 18, 21
]
const buildFilterLabel = (
	weekDays?: GridWeekDays[],
	hours?: number[]
) => {
	const labels = []
	if (weekDays && weekDays.length > 0) {
		labels.push(`${weekDays.join('')}曜`)
	}
	if (hours && hours.length > 0) {
		labels.push(
			hours.reduce<{
				from: number
				to: number
			}[]>(
				(acc, hour) => {
					if (acc.length === 0 || acc[acc.length - 1].to + 1 !== hour) {
						acc.push({ from: hour, to: hour + 2 })
					} else {
						acc[acc.length - 1].to = hour + 2
					}
					return acc
				},
				[]
			)
			.map(({ from, to }) => `${from}～${to}時`).join(' ')
		)
	}
	return labels.join(' / ')
}

type AccessTimeFilterProps<T> = {
	field: string
	fieldDef: GridColumnDef<T>
	columnsDef: GridColumnDefMap<T>
	dataList: T[]
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	values: any[] | null
	filter: GridFilter | undefined
	filters: GridFilter[]
	setFilters: (filters: GridFilter[]) => void
}
export const AccessTimeFilter = <T,>({
	field,
	fieldDef,
	columnsDef,
	dataList,
	values,
	filter,
	filters,
	setFilters
}: AccessTimeFilterProps<T>) => {
	const isCurrentWeekDay = useCallback(
		(index: number) => {
			if (!values) return false
			const weekDay = DayOfWeeks[index]
			const isCurrent = values.some(v => (typeof v !== 'string') ? false : v.startsWith(weekDay))
			return isCurrent
		},
		[values]
	)
	const isCurrentHour = useCallback(
		(index: number) => {
			if (!values) return false
			const hour = `${HourOfDays[index]}～${HourOfDays[index] + 2}時`
			const isCurrent = values.some(v => (typeof v !== 'string') ? false : v.endsWith(hour))
			return isCurrent
		},
		[values]
	)
	const {
		weekDaysHistgram,
		hoursHistgram
	} = useMemo(
		() => {
			let countMaxByWeekDay = 0
			const weekDaysHistgram: {
				value: GridWeekDays
				count: number
				rate: number
			}[] = DayOfWeeks.map(w => ({
				value: w,
				count: 0, rate: 0
			}))
			let countMaxByHour = 0
			const hoursHistgram: {
				value: number
				count: number
				rate: number
			}[] = HourOfDays.map(h => ({
				value: h,
				count: 0, rate: 0
			}))
			for(const record of dataList) {
				if (filters.some(filter => filter.field === field ? false : isFilterd(record, filter, columnsDef))) continue
				const value = fieldDef.value(record)
				if (!value) continue
				const array = Array.isArray(value) ? value : [value]
				for (const v of array) {
					if (typeof v !== 'string') continue
					const [dayOfWeek, hourOfDay] = v.split('\t')
					if (!dayOfWeek || !hourOfDay) continue

					const weekDay = weekDaysHistgram.find(w => dayOfWeek.startsWith(w.value))
					const hour = hoursHistgram.find(h => hourOfDay === `${h.value}～${h.value + 2}時`)

					if (weekDay) {
						if (!hour || !filter || !filter.hours || filter.hours.includes(hour.value)) {
							weekDay.count = weekDay.count + 1
							if (countMaxByWeekDay < weekDay.count) countMaxByWeekDay = weekDay.count
						}
					}
					if (hour) {
						if (!weekDay || !filter || !filter.weekDays || filter.weekDays.includes(weekDay.value)) {
							hour.count = hour.count + 1
							if (countMaxByHour < hour.count) countMaxByHour = hour.count
						}
					}					
				}
			}
			weekDaysHistgram.forEach(row => {
				row.rate = Math.ceil((row.count * 100) / countMaxByWeekDay)
			})
			hoursHistgram.forEach(row => {
				row.rate = Math.ceil((row.count * 100) / countMaxByHour)
			})
			return { weekDaysHistgram, hoursHistgram }
		},
		[columnsDef, filter, field, fieldDef, dataList, filters]
	)

	const onClickWeek = useCallback(
		(index: number) => {
			const weekDay = DayOfWeeks[index]
			if (filter) {
				const newWeekDays = !filter.weekDays ? (
					[weekDay]
				) : filter.weekDays.includes(weekDay) ? (
					filter.weekDays.filter(v => v !== weekDay)
				) : (
					filter.weekDays.concat(weekDay)
				)
				newWeekDays.sort((a, b) => DayOfWeeks.indexOf(a) - DayOfWeeks.indexOf(b))
				const newFilter: GridFilter = {
					...filter,
					weekDays: (newWeekDays.length === 0 || newWeekDays.length === DayOfWeeks.length) ? undefined : newWeekDays,
				}
				newFilter.label = buildFilterLabel(newFilter.weekDays, newFilter.hours)
				if (newFilter.weekDays || newFilter.hours) {
					// replace filter
					setFilters(filters.map(f => f.field === filter.field ? newFilter : f).slice())
				} else {
					// remove filter
					setFilters(filters.filter(f => f.field !== filter.field).slice())
				}
			} else {
				// new filter
				setFilters(filters.concat({
					field,
					label: buildFilterLabel([weekDay], undefined),
					weekDays: [weekDay]
				}).slice())
			}
		},
		[setFilters, field, filter, filters]
	)
	const onResetWeekDays = useCallback(
		() => {
			if (!filter) return
			if (filter.hours) {
				// replace filter
				const newFilter: GridFilter = {
					...filter,
					weekDays: undefined
				}
				newFilter.label = buildFilterLabel(newFilter.weekDays, newFilter.hours)
				setFilters(filters.map(f => f.field === filter.field ? newFilter : f).slice())
			} else {
				// remove filter
				setFilters(filters.filter(f => f.field !== filter.field).slice())
			}
		},
		[filter, setFilters, filters]
	)
	const onClickHour = useCallback(
		(index: number) => {
			const hour = HourOfDays[index]
			if (filter) {
				const newHours = !filter.hours ? (
					[hour]
				) : filter.hours.includes(hour) ? (
					filter.hours.filter(v => v !== hour)
				) : (
					filter.hours.concat(hour)
				)
				newHours.sort((a, b) => a - b)
				const newFilter: GridFilter = {
					...filter,
					hours: (newHours.length === 0 || newHours.length === HourOfDays.length) ? undefined : newHours
				}
				newFilter.label = buildFilterLabel(newFilter.weekDays, newFilter.hours)
				if (newFilter.weekDays || newFilter.hours) {
					// replace filter
					setFilters(filters.map(f => f.field === filter.field ? newFilter : f).slice())
				} else {
					// remove filter
					setFilters(filters.filter(f => f.field !== filter.field).slice())
				}
			} else {
				// new filter
				setFilters(filters.concat({
					field,
					label: buildFilterLabel(undefined, [hour]),
					hours: [hour]
				}).slice())
			}
		},
		[setFilters, field, filter, filters]
	)
	const onResetHours = useCallback(
		() => {
			if (!filter) return
			if (filter.weekDays) {
				// replace filter
				const newFilter: GridFilter = {
					...filter,
					weekDays: undefined
				}
				newFilter.label = buildFilterLabel(newFilter.weekDays, newFilter.hours)
				setFilters(filters.map(f => f.field === filter.field ? { ...filter, hours: undefined } : f).slice())
			} else {
				// remove filter
				setFilters(filters.filter(f => f.field !== filter.field).slice())
			}
		},
		[filter, setFilters, filters]
	)
	return (
		<Stack direction="column" flexGrow={1} overflow='auto' boxSizing="border-box" width='100%'>
			<Stack direction="column" padding={0}>
				{weekDaysHistgram.map((w, index) => (
					<ListItem key={`week-${index}`} disablePadding>
						<ListItemButton
							dense disableGutters sx={{ paddingY: 0, paddingLeft: 0, paddingRight: 0.5, margin: 0 }}
							onClick={() => onClickWeek(index)}
							disabled={w.count === 0}
						>
							<Checkbox
								size="small"
								checked={filter?.weekDays?.includes(w.value) ?? true}
								tabIndex={-1}
								disableRipple
								disabled={w.count === 0}
								inputProps={{ 'aria-labelledby': `check-week-${index}` }}
							/>
							<Box flexGrow={1} position='relative' overflow='hidden'>
								<Stack direction="row" width='100%' alignItems="center" position='relative' zIndex={1}>
									<ListItemText id={`check-week-${index}`} primary={`${w.value}曜日`} />
									<Typography variant="caption" whiteSpace="nowrap" paddingX={1}>{numberFormat(w.count)}件</Typography>
								</Stack>
								<Slide direction="left" in>
									<div
										key={`week-${index}-bar`}
										style={{
											position: 'absolute',
											right: 0,
											top: "calc(50% - (1.75em / 2))",
											width: `${w.rate}%`,
											height: '1.75rem',
											background: isCurrentWeekDay(index) ? 'rgb(225,225,245)' : 'rgb(235,235,235)',
											borderRadius: '4px 0 0 4px',
											transition: 'width 0.3s, background 0.3s',
											zIndex: 0
										}}
									/>
								</Slide>
							</Box>
						</ListItemButton>
					</ListItem>
				))}
			</Stack>

			<Divider/>

			<Stack direction="row" spacing={1} padding={1}>
				<Button
					fullWidth
					disabled={filter === undefined || filter.weekDays === undefined}
					variant="outlined"
					startIcon={<ReplayIcon />}
					sx={{ whiteSpace: 'nowrap' }}
					onClick={onResetWeekDays}
				>
					初期状態に戻す
				</Button>
			</Stack>

			<Divider/>

			<Stack direction="column" padding={0}>
				{hoursHistgram.map((h, index) => (
					<ListItem key={`hour-${index}`} disablePadding>
						<ListItemButton
							dense disableGutters sx={{ paddingY: 0, paddingLeft: 0, paddingRight: 0.5 }}
							onClick={() => onClickHour(index)}
							disabled={h.count === 0}
						>
							<Checkbox
								size="small"
								checked={filter?.hours?.includes(h.value) ?? true}
								tabIndex={-1}
								disableRipple
								disabled={h.count === 0}
								inputProps={{ 'aria-labelledby': `check-hour-${index}` }}
							/>
							<Box flexGrow={1} position='relative' overflow='hidden'>
								<Stack direction="row" width='100%' alignItems="center" position='relative' zIndex={1}>
									<ListItemText id={`check-week-${index}`} primary={`${h.value}～${h.value + 2}時`} />
									<Typography variant="caption" whiteSpace="nowrap" paddingX={1}>{numberFormat(h.count)}件</Typography>
								</Stack>
								<Slide direction="left" in>
									<div
										key={`hour-${index}-bar`}
										style={{
											position: 'absolute',
											right: 0,
											top: "calc(50% - (1.75em / 2))",
											width: `${h.rate}%`,
											height: '1.75rem',
											background: isCurrentHour(index) ? 'rgb(225,225,245)' : 'rgb(235,235,235)',
											borderRadius: '4px 0 0 4px',
											transition: 'width 0.3s, background 0.3s',
											zIndex: 0
										}}
									/>
								</Slide>
							</Box>
						</ListItemButton>
					</ListItem>
				))}
			</Stack>

			<Divider/>

			<Stack direction="row" spacing={1} padding={1}>
				<Button
					fullWidth
					disabled={filter === undefined || filter.hours === undefined}
					variant="outlined"
					startIcon={<ReplayIcon />}
					sx={{ whiteSpace: 'nowrap' }}
					onClick={onResetHours}
				>
					初期状態に戻す
				</Button>
			</Stack>
		</Stack>
	)
}

